import {
  interfaceTourSteps,
  LS_ONBOARDING_DONE,
  LS_SUPPORT_TOUR_DONE,
  LS_TELEGRAM_TOUR_DONE,
  OnboardingTour,
  PhoneOnboardingModal,
  supportTourSteps,
  telegramTourSteps,
  useOnboardingState,
} from "@features/onboarding";
import { SupportFloatButton } from "@features/support";
import type { ThemeMode } from "@shared/store/uiStore";
import { useStoredUser } from "@shared/useStoredUser";
import { Grid, Layout, theme } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { HeaderBar } from "./HeaderBar";
import { MobileNavDrawer } from "./MobileNavDrawer";
import { SiderMenu } from "./SiderMenu";

const { Content } = Layout;

interface MainLayoutProps {
  mode: ThemeMode;
  onModeChange: (mode: ThemeMode) => void;
}

const MainLayout = ({ mode, onModeChange }: MainLayoutProps) => {
  const storedUser = useStoredUser();
  const location = useLocation();

  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const lastPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (lastPathnameRef.current !== location.pathname) {
      lastPathnameRef.current = location.pathname;
      setMobileNavOpen(false);
    }
  }, [location.pathname]);

  const step = useOnboardingState(storedUser);

  const handleTelegramTourClose = useCallback(() => {
    localStorage.setItem(LS_TELEGRAM_TOUR_DONE, "true");
    window.location.reload();
  }, []);

  const handleSupportTourClose = useCallback(() => {
    localStorage.setItem(LS_SUPPORT_TOUR_DONE, "true");
    window.location.reload();
  }, []);

  const handleInterfaceTourClose = useCallback(() => {
    localStorage.setItem(LS_ONBOARDING_DONE, "true");
    window.location.reload();
  }, []);

  return (
    <Layout style={{ minHeight: "100vh", background: token.colorBgLayout }}>
      {isMobile ? (
        <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      ) : (
        <SiderMenu />
      )}
      <Layout>
        <HeaderBar
          user={storedUser}
          mode={mode}
          onModeChange={onModeChange}
          isMobile={isMobile}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <Content style={{ padding: isMobile ? 8 : 16, background: token.colorBgLayout }}>
          <Outlet />
        </Content>
      </Layout>
      <SupportFloatButton />
      <PhoneOnboardingModal open={step === "phone"} />
      <OnboardingTour
        open={step === "telegram-tour"}
        onClose={handleTelegramTourClose}
        steps={telegramTourSteps}
      />
      <OnboardingTour
        open={step === "support-tour"}
        onClose={handleSupportTourClose}
        steps={supportTourSteps}
      />
      <OnboardingTour
        open={step === "interface-tour"}
        onClose={handleInterfaceTourClose}
        steps={interfaceTourSteps}
      />
    </Layout>
  );
};

export default MainLayout;
